import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';

import {
  filter,
  includes,
  map,
  uniq,
  intersection,
  keyBy,
  mapValues,
  find,
} from 'lodash';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Alert from 'react-bootstrap/Alert';
import {css} from 'aphrodite';

import {
  getStatePayerCoverageList,
  getStateCoverageProfiles,
  setStateCoverageShowAlert,
  ERROR_ALERT,
  NO_ROW_ALERT,
} from 'actions/coverage';

import {
  getError,
  getInProgress,
  getLastRefreshedDate,
  getShowErrorAlert,
  getShowNoRowsAlert,
} from 'selectors/coverage';

import {
  filterForMapStates,
  orderData,
  renderMultifieldString,
  validateObjectProperties,
} from 'libs/salmon-utils';

import {getSettings} from 'selectors/settings';

import {getErrorField, hasClass} from 'libs/react-widgets';
import TabularDataBasic from 'components/TabularDataBasic';
import USMap, {USStates} from 'components/USMap';
import Markdown from 'components/Markdown';
import Isi from 'components/Isi';
import ErrorAlert from 'components/ErrorAlert';
import StateCoverageProfileDocumentDownload from 'components/StateCoverageProfileDocumentDownload';
import CoverageFilters, {getFilteredValues} from './Filters';
import {applyDynamicFilters} from './Filters/utils';
import {
  buildUrlParameters,
  parseUrlParameters,
  updateBrowserUrl,
} from './Filters/url';
import TableFooter from './TableFooter';
import {
  ExitModal,
  FeatureModal,
  DisclaimerModal,
  ContactInstructionsModal,
} from '../Modals';
import Loader from 'components/Loader';
import './styles.scss';
import {sortProducts, triStateDisplay, getCoverageColumns} from './table-formatter';
import {mapCoverageSummaryModalData} from './CoverageSummary';
import {ButtonGroup} from './ButtonGroup';

export class Coverage extends Component {

  constructor(props) {
    super(props);

    this.state = {
      selectedPayers: [], // @deprecated multi-select values
      selectedStates: [],
      selectedIndications: [], // @deprecated
      selectedPlanTypes: [],
      selectedProducts: [], // @deprecated
      selectedPbms: [], // @deprecated
      ...(props.settings.table.dynamicColumns && {selectedDynamicFilters: {}}),
      previousSelectedProducts: null,
      // Note: filteredX values are no longer stored in state - they are computed
      // dynamically from selectedX values in the render() method
      results: false,
      coverageSummary: {},
      showExitModal: false,
      showSummaryModal: false,
      showContactInstructionsModal: false,
      showDisclaimerModal: false,
      showFeatureModal: this.props.coveragePositions.length === 0 ? !props.settings.newFeatureModal.disabled : false,
      showInstruction: true,
      urlParameters: parseUrlParameters(),
    };

    this._handleHide = this._handleHide.bind(this);
    this._handleDocumentClick = this._handleDocumentClick.bind(this);
    this._handleContactInstructionsClick = this._handleContactInstructionsClick.bind(this);
    this._handleOpenDocument = this._handleOpenDocument.bind(this);
    this._handleOpenMedicalCoverage = this._handleOpenMedicalCoverage.bind(this);
    this._filterCoverage = this._filterCoverage.bind(this);
    this._handleUSMapClick = this._handleUSMapClick.bind(this);
  }

  componentDidUpdate(prevProps, prevState) {
    // Only handle results flag computation here
    // Dropdown selection logic moved to CoverageFilters

    // Check if selectedX values changed
    const selectedFiltersChanged =
      JSON.stringify(this.state.selectedPayers) !== JSON.stringify(prevState.selectedPayers) ||
      JSON.stringify(this.state.selectedStates) !== JSON.stringify(prevState.selectedStates) ||
      JSON.stringify(this.state.selectedProducts) !== JSON.stringify(prevState.selectedProducts) ||
      JSON.stringify(this.state.selectedIndications) !== JSON.stringify(prevState.selectedIndications) ||
      JSON.stringify(this.state.selectedPlanTypes) !== JSON.stringify(prevState.selectedPlanTypes) ||
      JSON.stringify(this.state.selectedPbms) !== JSON.stringify(prevState.selectedPbms) ||
      (this.props.settings.table.dynamicColumns &&
       JSON.stringify(this.state.selectedDynamicFilters) !== JSON.stringify(prevState.selectedDynamicFilters));

    // If URL params exist and selections changed, compute results flag
    if (selectedFiltersChanged && Object.keys(this.state.urlParameters).length > 0) {
      // Compute results based on whether any filters have valid selections
      const hasValidSelections =
        this.state.selectedPayers.length > 0 ||
        this.state.selectedStates.length > 0 ||
        this.state.selectedProducts.length > 0 ||
        this.state.selectedIndications.length > 0 ||
        this.state.selectedPlanTypes.length > 0 ||
        this.state.selectedPbms.length > 0 ||
        (this.props.settings.table.dynamicColumns &&
         Object.values(this.state.selectedDynamicFilters || {}).some((arr) => arr.length > 0));

      this.setState({
        results: hasValidSelections
      });
    }
  }

  componentDidMount() {
    const {
      dispatchGetStatePayerCoverageList,
      dispatchGetStateCoverageProfiles,
      settings,
    } = this.props;

    // don't fetch again if component remounts
    if (this.props.coveragePositions.length === 0) {
      if (settings.domain) {
        dispatchGetStatePayerCoverageList(settings.domain, settings.content.searchErrorUnavailable);
        if (settings.stateCoverageProfiles && settings.stateCoverageProfiles.enabled) {
          dispatchGetStateCoverageProfiles(settings.domain);
        }
      } else if (settings.drugId) {
        dispatchGetStatePayerCoverageList(settings.drugId, settings.content.searchErrorUnavailable);
      }
    }
  }

  _handleFilterChange = (filterUpdates) => {
    // Callback from CoverageFilters to update filter state
    this.setState(filterUpdates);
  }

  _handleUrlFiltersUpdate = (selectedFilters) => {
    // Compute results flag based on whether filters have valid selections
    const hasValidSelections = Object.entries(selectedFilters).some(([key, value]) => {
      if (key === 'selectedDynamicFilters') {
        // Handle selectedDynamicFilters object: check if any filter has selections
        return value && Object.values(value).some((arr) => Array.isArray(arr) && arr.length > 0);
      }
      return Array.isArray(value) && value.length > 0;
    });

    this.setState({
      ...selectedFilters,
      results: hasValidSelections
    });
  }

  _handleMapStateClick(el) {
    const {
      selectedStates,
    } = this.state;

    const currentElement = el.currentTarget;
    const clickedState = find(this.props.usStates, {id: currentElement.id});

    let currentSelectedStates = [...selectedStates];
    if (hasClass(currentElement, 'active')) {
      currentSelectedStates = currentSelectedStates.filter(
        (state) => state.id !== currentElement.id
      );
    } else {
      currentSelectedStates = [...currentSelectedStates, clickedState];
    }

    this.setState({
      selectedStates: currentSelectedStates,
    });
  }

  _filterCoverage() {
    const {
      settings,
    } = this.props;

    // Update browser url parameters.
    const newUrlParameters = buildUrlParameters(
      this.state,
      settings.filterLayout,
      settings.urlParametersMap
    );
    updateBrowserUrl(newUrlParameters);

    // Note: filteredX values are no longer stored in state - they are computed
    // dynamically from selectedX values in the render() method
    this.setState({
      results: true,
      showInstruction: settings.content.alwaysShowInstruction,
      urlParameters: newUrlParameters,
    }, () => {
      this._setAlert(NO_ROW_ALERT, true);
      if (this.props.settings.jumpToResultsAfterSearching) {
        document.getElementById('results').scrollIntoView();
      }
    });
  }

  _setAlert(type, value) {
    this.props.dispatchSetStateCoverageShowAlert({
      type,
      value,
    });
  }

  // Adds text property to the fields and populate it from rowData.
  _addTextToFields(fields, rowData) {
    return fields.map((el) => {
      let data = rowData[el.property];
      if (Array.isArray(data)) {
        el.text = data.length > 0 ? data.sort().join(', ') : el.default;
      } else {
        el.text = data ? data : el.default;
      }
      return el;
    });
  }

  _handleUSMapClick(el) {
    const {
      selectedStates,
    } = this.state;

    const currentElement = el.currentTarget;
    let include = !hasClass(currentElement, 'active');

    let currentSelectedStates = filterForMapStates(selectedStates, currentElement.id, include, USStates);

    this.setState({
      selectedStates: currentSelectedStates,
    });
  }

  _handleHide() {
    this.setState({
      showExitModal: false,
      showSummaryModal: false,
      showFeatureModal: false,
      showDisclaimerModal: false,
      showContactInstructionsModal: false,
    });
  }

  _handleDocumentClick(e) {
    e.preventDefault();

    this.setState({
      showExitModal: true,
      documentLink: e.currentTarget.href,
    });
  }

  _handleContactInstructionsClick(e) {
    e.preventDefault();
    this.setState({
      showContactInstructionsModal: true,
      contactInstructionsClickedId: e.currentTarget.id,
    });
  }

  _handleOpenDocument() {
    window.open(this.state.documentLink, '_blank', 'noopener,noreferrer');

    this.setState({
      showExitModal: false,
    });
  }

  _handleOpenMedicalCoverage(settings, rowData, storageID) {
    const lastRefreshedDate = this.props.lastRefreshedDate;
    let disclaimer = settings.content.medicalNecessity.disclaimer;
    if (lastRefreshedDate) {
      disclaimer = disclaimer.replace(/\[\[(.*)\]\]/, lastRefreshedDate);
    }

    // Expire the data in 1 hour.
    const expiry = new Date().getTime() + 3600000;

    const coverageData = {
      fillInTheBlanks: settings.content.medicalNecessity.fillInTheBlanks,
      title: renderMultifieldString(settings.content.medicalNecessity.title, rowData, settings.content.medicalNecessity.allStates),
      fields: this._addTextToFields(settings.content.medicalNecessity.fields, rowData),
      disclaimer: disclaimer,
      medicalNecessityStyles: settings.styles.medicalNecessityChecklist,
      header: {
        logo: settings.header.productLogo,
        style: settings.styles.headerLogo,
      },
      expiry: expiry,
    };

    const sID = storageID ? storageID : Math.floor(Math.random() * 1000000);
    localStorage.setItem(`medicalNecessityChecklist-${sID}`, JSON.stringify(coverageData));
    window.open(`medicalNecessityChecklist?id=${sID}`, '_blank', 'noopener,noreferrer');

    return sID;
  }

  render() {
    const {
      selectedStates,
      selectedPayers,
      selectedIndications,
      selectedPlanTypes,
      selectedProducts,
      selectedPbms,
      results,
      contactInstructionsClickedId,
      showExitModal,
      showContactInstructionsModal,
      showFeatureModal,
      showDisclaimerModal,
      showInstruction,
    } = this.state;

    const {
      inProgress,
      usStates,
      planTypes,
      indications,
      coveragePositions,
      stateCoverageProfiles,
      settings,
      styleSheet,
      error,
      showErrorAlert,
      showNoRowsAlert,
    } = this.props;

    // Extract visible columns from settings
    const visibleColumns = settings.table.columns.map((column) => column.property);

    // Compute filtered values from selected values using helper method
    // This allows us to derive filteredX from selectedX without storing them separately
    const computedFilteredValues = getFilteredValues(
      {
        selectedPayers,
        selectedStates,
        selectedIndications,
        selectedPlanTypes,
        selectedProducts,
        selectedPbms,
      },
      visibleColumns,
      planTypes
    );

    // Use computed values instead of state values
    // Note: filteredX values are computed dynamically from selectedX values
    // based on visible columns, eliminating the need to store them separately in state
    const {
      filteredPayers,
      filteredStates,
      filteredIndications,
      filteredPlanTypes,
      filteredProducts,
      filteredPbms,
    } = computedFilteredValues;

    let contactInstructionsHashMap = {};
    let tableRows = [];

    if (results && this.props.usStates.length) {
      const indicationsHashMap = indications.reduce((acc, indication) => {
        let id = indication.id;
        acc[id] = indication.label;
        return acc;
      }, {});

      // Reducer callback for recursively reducing filteredPlanTypes into set of all matching plantypes,
      // including all descendants
      const recurseChildrenPlanType = (acc, planType) => {
        let childrenPlanTypes = map(filter(planTypes, ['parentname', planType]), 'label');
        return acc.concat(childrenPlanTypes.reduce(recurseChildrenPlanType, childrenPlanTypes));
      };
      const matchingPlanTypes = map(filteredPlanTypes, 'label').reduce(recurseChildrenPlanType, map(filteredPlanTypes, 'label'));

      // Filter coverage positions using computed filteredX values (derived from selectedX + visible columns)
      let filteredPositions = coveragePositions.reduce((acc, val) => {
        if ((filteredPayers.length === 0) || includes(map(filteredPayers, 'id'), val.payerid)) {
          if ((filteredIndications.length === 0) || (intersection(map(filteredIndications, 'id'), val.indicationIds).length !== 0)) {
            let filteredNodes = filter(val.presenceNodes, (node) => {
              // Filter presenceNodes by states. No filter corresponding to the filter array being empty
              let stateExist = (filteredStates.length === 0) ? true : includes(map(filteredStates, 'id'), node.stateAbbr);
              // Filter presenceNodes by plantypes, including node if plantype (not originalPlanType)
              // matches one of the pre-generated matching plantypes array
              let planExist = (filteredPlanTypes.length === 0) ? true : includes(matchingPlanTypes, node.planType);

              return planExist && stateExist;
            });

            // Filter for indications and use indication label in column data
            if (filteredIndications.length !== 0) {
              val.indications = filteredIndications.reduce((acc, indicationId) => {
                if (includes(val.indicationIds, indicationId.id)) {
                  acc.push(indicationId.label);
                }

                return acc;
              }, []);
            } else {
              val.indications = val.indicationIds.map((id) => indicationsHashMap[id]);
            }

            acc.push({
              ...val,
              presenceNodes: filteredNodes,
            });
          }
        }

        return acc;
      }, []);

      if (filteredProducts.length !== 0) {
        const productIds = filteredProducts.map((x) => x.id);
        filteredPositions = filteredPositions.filter( (position) => {
          return productIds.includes(position.product.id);
        });
      }

      // Filter for positions by pbm.
      if (filteredPbms.length !== 0) {
        const pbmIds = filteredPbms.map((x) => x.id);
        filteredPositions = filteredPositions.filter( (position) => {
          return pbmIds.includes(position?.pbmid);
        });
      }

      // Apply dynamic column filters if dynamicColumns enabled
      if (settings.table.dynamicColumns) {
        filteredPositions = applyDynamicFilters(
          filteredPositions,
          this.state.selectedDynamicFilters || {},
          settings
        );
      }

      /* Check if we do rollup per #2281 or display plans that match the filteredPlanTypes per #7620 */
      // Default behaviour
      let dynamicRollupEnabled = false;
      if (settings.filterLayout && settings.filterLayout.length) {
        const filterLayout = settings.filterLayout;
        const planFilterSetting = filterLayout.find((filterSetting) => {
          return (filterSetting.filter === 'plan');
        });
        dynamicRollupEnabled = (planFilterSetting && planFilterSetting.dynamicRollup);
      }
      // get parent nodes from planTypes tree
      const parentPlanTypeNames = map(
        planTypes.filter((planType) => planType.parentname === null),
        (parentPlanType) => parentPlanType.label
      );
      // Check if selected filters are parent plan types
      // If even 1 filtered plan type is not a parent, return true
      const hasChildFilter = filteredPlanTypes.some((filteredPlanType) => {
        return (parentPlanTypeNames.indexOf(filteredPlanType.label) === -1);
      });

      filteredPositions.forEach((position) => {
        // Dynamically check if we do rollup per #2281 or display plans that match the filteredPlanTypes per #7620
        const planTypeMustMatchFilteredPlanTypes = dynamicRollupEnabled && hasChildFilter;
        let planTypes = [];

        // Get planTypes that match the actual plan type values the user filtered for.
        if (planTypeMustMatchFilteredPlanTypes) {
          let validPlanTypes = new Set(map(filteredPlanTypes, 'label'));

          let planTypeMatches = [];
          position.presenceNodes.forEach((val) => {
            if (validPlanTypes.has(val.planType)) {
              planTypeMatches.push(val.planType);
            }
            if (validPlanTypes.has(val.originalPlanType)) {
              planTypeMatches.push(val.originalPlanType);
            }
          });

          planTypes = uniq(planTypeMatches);
        } else {
          planTypes = uniq(map(position.presenceNodes, 'originalPlanType'));
        }

        planTypes.forEach((currentPlanType) => {
          let states = position.presenceNodes.reduce((acc, val) => {
            if ((val['originalPlanType'] === currentPlanType) ||
               (planTypeMustMatchFilteredPlanTypes && val['planType'] === currentPlanType)) {
              if (!includes(acc, val.stateAbbr)) {
                acc.push(val.stateAbbr);
              }
            }
            return acc;
          }, []);

          states.sort();

          let salmonDocumentsData = [];
          const showSalmonDocuments = settings.table.columns.filter((el) => el.property === 'salmonDocuments');
          if (showSalmonDocuments.length) {
            salmonDocumentsData = orderData(position.salmonDocuments, 'title', showSalmonDocuments[0].titleOrder)
          }

          // If you update this, you will likely also need to update
          // src/libs/salmon-utils.js:coveragePositionTemplate
          // for data consistency.
          let row = {
            id: `${position.coveragepositionid}-${position.payer}-${currentPlanType}`,
            benefitType: position.benefitType,
            contacts: position.contacts,
            contactModalData: {
              expeditedpa: position.expeditedpa,
              peertopeer: position.peertopeer,
              contactinstructions: position.contactinstructions,
            },
            covered: position.covered,
            coveredMedicalCodes: position.coveredMedicalCodes,
            criteria: position.criteria,
            criteriaMarkdown: position.criteriamarkdown,
            documentUrl: {
              policyUrl: position.policyUrl,
              priorAuthorizationUrl: position.priorAuthorizationUrl,
            },
            indications: position.indications,
            payer: position.payer,
            planType: currentPlanType,
            priorAuthorizationText: position.priorAuthorizationText,
            priorAuthorization: position.priorAuthorizationRequired,
            states,
            product: position.product === undefined ? null : position.product.name,
            verballyConfirmed: position.verballyConfirmed,
            lastReviewDate: position.lastReviewDate,
            salmonDocuments: salmonDocumentsData,
            stepEditText: position.stepEditText,
            pbm: position.pbm,
            ...mapCoverageSummaryModalData({position, states, planType: currentPlanType})
          };

          // ============Dynamic columns start============
          // Map dynamic fields from position.fields array to row data
          if (settings.table.dynamicColumns && position.fields && Array.isArray(position.fields)) {
            // Add the fields array to rowData for access to full field structure
            row.fields = position.fields;

            // Build a lookup map of fieldName -> value from position.fields
            const fieldsMap = {};
            position.fields.forEach((field) => {
              if (field.fieldName && field.value !== undefined && field.value !== null) {
                fieldsMap[field.fieldName] = field.value;
              }
            });

            // Map dynamic fields to row based on column properties
            settings.table.columns.forEach((column) => {
              const fieldName = column.property;
              // Skip if property already exists in row (static field)
              if (!row.hasOwnProperty(fieldName) && fieldsMap.hasOwnProperty(fieldName)) {
                row[fieldName] = fieldsMap[fieldName];
              }
            });
          }
          // ============Dynamic columns end============

          contactInstructionsHashMap[`${position.coveragepositionid}-${position.payer}-${currentPlanType}`] = {
            ...mapValues(keyBy(position.contacts, 'type'), 'contact'), // Add all available phone numbers as attributes.
            expeditedpa: triStateDisplay(position.expeditedpa, null),
            peertopeer: triStateDisplay(position.peertopeer, null),
            contactinstructions: position.contactinstructions,
          };

          tableRows.push(row);
        });
      });
    }

    const tableColumns = getCoverageColumns({
      props: this.props,
      handleDocumentClick: this._handleDocumentClick,
      handleOpenMedicalCoverage: this._handleOpenMedicalCoverage,
      handleContactInstructionsClick: this._handleContactInstructionsClick,
      openDisclaimerModal: () => this.setState({showDisclaimerModal: true}),
    });

    const tableFooter = (
      <TableFooter
        footerColSpan={tableColumns.length}
        styleSheet={styleSheet}
        selectedProductId={filteredProducts.length > 0 ? filteredProducts[0].id : settings.drugId}
        disclaimerTemplate={settings.content.tableDisclaimerExpanded.markdown}
      />
    );

    /**
     * TODO: Remove planTypes from CoverageFilters props.
     */
    const filterInputs = (
      <CoverageFilters
        planTypes={planTypes}
        selectedStates={selectedStates}
        selectedPayers={selectedPayers}
        selectedIndications={selectedIndications}
        selectedPlanTypes={selectedPlanTypes}
        selectedProducts={selectedProducts}
        selectedPbms={selectedPbms}
        selectedDynamicFilters={settings.table.dynamicColumns ? (this.state.selectedDynamicFilters || {}) : undefined}
        inProgress={inProgress}
        settings={settings}
        styleSheet={styleSheet}
        urlParameters={this.state.urlParameters}
        onUrlFiltersUpdate={this._handleUrlFiltersUpdate}
        onFilterChange={this._handleFilterChange}
      />
    );

    /*
     * TODO: replace modalSuccess with buttonPrimary for the shared search button style
     * should be like this:
     * const searchButtonStyle = css(styleSheet.buttonPrimary, styleSheet.searchButton);
     * https://github.com/PolicyReporter/requests/issues/18118
     */
    const searchButtonStyle = css(styleSheet.buttonPrimary, styleSheet.modalSuccess, styleSheet.searchButton);
    const isSearchButtonDisabled = inProgress || (Array.isArray(settings.drugId) && selectedProducts.length === 0);

    const getCellClass = (cell) => {
      const {original: rowData} = cell;
      // If rowData is verballyConfirmed, highlight it.
      if (rowData.verballyConfirmed) {
        return css(this.props.styleSheet.highlightRow)
      }

      // If the rowData meets the highlightRowCriteria, highlight it.
      let highlightClass = null;
      if (this.props.settings.table.highlightRowCriteria) {
        for (const rowCriteria of this.props.settings.table.highlightRowCriteria) {
          if (validateObjectProperties(rowData, rowCriteria)) {
            highlightClass = css(this.props.styleSheet.highlightRow);
            break;
          }
        }
        return highlightClass;
      }
    }

    return (
      <div data-component={'Coverage'}>
        <Loader isFetching={inProgress} isFullscreen/>

        <ExitModal
          settings={settings}
          styleSheet={styleSheet}
          showExitModal={showExitModal}
          handleHide={this._handleHide}
          handleOpenDocument={this._handleOpenDocument}
        />

        <FeatureModal
          settings={settings}
          styleSheet={styleSheet}
          showFeatureModal={showFeatureModal}
          handleHide={this._handleHide}
        />

        <DisclaimerModal
          settings={settings}
          styleSheet={styleSheet}
          showDisclaimerModal={showDisclaimerModal}
          handleHide={this._handleHide}
        />

        <ContactInstructionsModal
          settings={settings}
          styleSheet={styleSheet}
          showContactInstructionsModal={showContactInstructionsModal}
          handleHide={this._handleHide}
          rowData={contactInstructionsHashMap[contactInstructionsClickedId]}
        />

        <Container>
          <Row>
            {Boolean(settings.banner.enabled) &&
            <Col xs={12} className={css(styleSheet.banner)}>
              { Boolean(settings.banner.content.html) &&
                <div dangerouslySetInnerHTML={{__html: settings.banner.content.html}} />
              }
            </Col>
            }
          </Row>
          <Row>
            {
              settings.content.searchPreface &&
              <div className={`searchPreface ${css(styleSheet.searchPreface)}`}>
                <Markdown>{settings.content.searchPreface}</Markdown>
              </div>
            }
            {
              showInstruction &&
              <div className={`instruction ${css(styleSheet.initialSearchInstruction)}`}>
                <Markdown>
                  {settings.content.initialSearchInstruction}
                </Markdown>
              </div>
            }
            <ErrorAlert error={getErrorField('global', 'global', error)} alertVisible={showErrorAlert} handleDismissAlert={this._setAlert.bind(this, ERROR_ALERT, false)}/>
          </Row>
          {!settings.searchCoverageFunctionality.disabled &&
            <React.Fragment>
              {filterInputs}
              {!results && settings.content.underFiltersText?.markdown &&
                <div>
                  <Row>
                    <Col>
                      <div className={css(styleSheet.underFiltersText)}>
                        <Markdown>{settings.content.underFiltersText.markdown}</Markdown>
                      </div>
                    </Col>
                  </Row>
                </div>
              }
              <Row className={'text-center'}>
                <Col className="w-auto mx-auto">
                  <Button
                    type={'submit'}
                    className={ isSearchButtonDisabled ? `${searchButtonStyle} disabled` : searchButtonStyle }
                    disabled={ isSearchButtonDisabled }
                    onClick={ this._filterCoverage }
                  >
                  Search
                  </Button>
                </Col>
              </Row>
            </React.Fragment>
          }
          {settings.usRegionalMap &&
            <Row>
              <Col className='p-0'>
                <USMap
                  handleStateClick={this._handleUSMapClick}
                  selectedStates={selectedStates}
                />
              </Col>
            </Row>
          }

          {showInstruction &&
            <Row className={css(styleSheet.initialSupplementalInstruction)}>
              <Markdown>
                {settings.content.initialSupplementalInstruction.markdown}
              </Markdown>
            </Row>
          }
          {results &&
            <Row className={css(styleSheet.results)} id='results'>
              {(!inProgress && tableRows.length === 0 && showNoRowsAlert) &&
                <Row className='p-0 m-0'>
                  <Alert variant={'warning'} dismissible onClose={this._setAlert.bind(this, NO_ROW_ALERT, false)}>{settings.content.tableErrorNoResult}</Alert>
                </Row>
              }
              {(settings.stateCoverageProfiles.enabled && stateCoverageProfiles.length) &&
                <>
                  <StateCoverageProfileDocumentDownload
                    settings={settings}
                    styleSheet={styleSheet}
                    stateCoverageProfiles={stateCoverageProfiles}
                  />
                </>
              }
              {tableRows.length > 0 &&
                <div className='p-0'>
                  <Row>
                    <div className={`${css(styleSheet.footNote)} p-0`}>
                      <Markdown>{settings.content.tableDisclaimerShort.markdown}</Markdown>
                    </div>
                  </Row>
                  <Row>
                    <div className={`${css(styleSheet.dataTable)} p-0`}>
                      <TabularDataBasic
                        data={tableRows}
                        columns={tableColumns}
                        getCellClass={getCellClass}
                        customFooter={tableFooter}
                        onPageChangeCallback={() => document.getElementById('results').scrollIntoView({behavior: settings.styles.page.scrollBehavior})}
                        renderToolbar={
                          settings.table.buttons?.length > 0
                            ? (visibleRows) => (
                              <ButtonGroup
                                buttons={settings.table.buttons}
                                data={visibleRows}
                                styleSheet={styleSheet}
                              />
                            )
                            : undefined
                        }
                      />
                    </div>
                  </Row>
                </div>
              }
              {settings.content.underResultsText &&
                <div>
                  <Row>
                    <Col>
                      <div className={css(styleSheet.underResultsText)}>
                        <Markdown>{settings.content.underResultsText.markdown}</Markdown>
                      </div>
                    </Col>
                  </Row>
                </div>
              }
            </Row>
          }
          <Isi settings={settings.isi} styleSheet={styleSheet}/>
          {settings.content.bottomSection &&
            <Row><div className={css(styleSheet.bottomSection)}>
              <Markdown>{settings.content.bottomSection.markdown}</Markdown>
            </div></Row>
          }
        </Container>
      </div>
    );
  }
}

Coverage.propTypes = {
  dispatchGetStatePayerCoverageList: PropTypes.func.isRequired,
  dispatchGetStateCoverageProfiles: PropTypes.func.isRequired,
  dispatchSetStateCoverageShowAlert: PropTypes.func.isRequired,
  coveragePositions: PropTypes.array.isRequired,
  stateCoverageProfiles: PropTypes.array.isRequired,
  lastRefreshedDate: PropTypes.string.isRequired,
  usStates: PropTypes.array,
  planTypes: PropTypes.array,
  indications: PropTypes.array,
  settings: PropTypes.object.isRequired,
  styleSheet: PropTypes.object.isRequired,
  error: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.bool,
    PropTypes.string,
  ]),
  showErrorAlert: PropTypes.bool.isRequired,
  showNoRowsAlert: PropTypes.bool.isRequired,
};

const mapStateToProps = function (state, ownProps) {
  const {
    coverage,
  } = state;

  return {
    stateCoverageProfiles: coverage.get('stateCoverageProfiles').toJS(),
    coveragePositions: coverage.get('coveragePositions').toJS(),
    usStates: coverage.get('usStates').toJS(),
    indications: coverage.get('indications').toJS(),
    planTypes: coverage.get('planTypes').toJS(),
    error: getError(state),
    lastRefreshedDate: getLastRefreshedDate(state),
    inProgress: getInProgress(state),
    settings: getSettings(state),
    showErrorAlert: getShowErrorAlert(state),
    showNoRowsAlert: getShowNoRowsAlert(state),
  };
};

const mapDispatchToProps = function (dispatch) {
  return {
    dispatchGetStatePayerCoverageList: (...args) => {
      dispatch(getStatePayerCoverageList(...args));
    },
    dispatchGetStateCoverageProfiles: (...args) => {
      dispatch(getStateCoverageProfiles(...args));
    },
    dispatchSetStateCoverageShowAlert: (...args) => {
      dispatch(setStateCoverageShowAlert(...args));
    },
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Coverage);

// Re-export helper functions for backward compatibility
export {restrictPayers, extractPbms} from './Filters/utils';
